import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, MapPin, Phone, FileBadge, Clock } from "lucide-react";
import { IMAGES, COMPANY, API_URL } from "../data/site";
import { PageHero } from "../components/PageHero";
import { Reveal, Eyebrow } from "../components/Reveal";

const inputCls =
  "w-full border border-white/15 bg-obsidian px-4 py-3 text-sm text-white placeholder:text-slate-500 outline-none transition-colors focus:border-blueprint";

const SUBJECTS = [
  "General Question",
  "Hiring / Careers",
  "Billing / Payroll",
  "Media / Partnership",
  "Other",
];

const INFO = [
  { icon: MapPin, label: "Headquarters", value: COMPANY.address, testId: "contact-address" },
  { icon: Phone, label: "Office", value: COMPANY.phone, href: COMPANY.phoneHref, testId: "contact-phone" },
  { icon: FileBadge, label: "License", value: COMPANY.license, testId: "contact-license" },
  { icon: Clock, label: "Office Hours", value: "Mon–Fri · Paso Robles HQ", testId: "contact-office-note" },
];

export default function Contact() {
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/enquiries`, { ...form, formType: "contact" });
      setDone(true);
      toast.success("Message sent — our office has it.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed. Please call our office instead.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHero
        testId="contact-hero"
        eyebrow="Contact // Paso Robles HQ"
        lines={["Get in", "touch."]}
        sub="General questions, hiring, or anything else — send us a note and our office will get back to you. Looking to bid a project? Head to Request a Bid."
        image={IMAGES.heroAerial}
      />

      <section data-testid="contact-main" className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-[1fr_1.4fr] lg:gap-16">
            <div>
              <Reveal>
                <Eyebrow>Direct Lines</Eyebrow>
                <h2 className="mt-4 font-display text-4xl font-extrabold uppercase tracking-tight text-white">
                  {COMPANY.name}
                </h2>
              </Reveal>
              <div className="mt-8 grid gap-px border border-white/10 bg-white/10">
                {INFO.map((item, i) => (
                  <Reveal key={item.label} delay={i * 0.05}>
                    <div className="flex items-start gap-4 bg-obsidian p-5">
                      <item.icon className="mt-0.5 h-5 w-5 shrink-0 text-brand-light" />
                      <div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
                          {item.label}
                        </p>
                        {item.href ? (
                          <a href={item.href} data-testid={item.testId} className="mt-1 block text-sm text-blueprint hover:text-white">
                            {item.value}
                          </a>
                        ) : (
                          <p data-testid={item.testId} className="mt-1 text-sm text-slate-200">{item.value}</p>
                        )}
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
              <Reveal delay={0.2}>
                <Link
                  to="/request-bid"
                  data-testid="contact-to-bid-link"
                  className="mt-6 inline-flex items-center gap-2 border border-brand/50 bg-brand/10 px-5 py-4 text-sm font-semibold text-white transition-colors hover:bg-brand hover:border-brand"
                >
                  Have a project? Request a Bid
                  <ArrowUpRight className="h-4 w-4" />
                </Link>
              </Reveal>
            </div>

            <Reveal delay={0.1}>
              {done ? (
                <div
                  data-testid="contact-success"
                  className="blueprint-grid flex h-full min-h-[24rem] flex-col items-center justify-center border border-blueprint/30 bg-obsidian p-10 text-center"
                >
                  <p className="font-mono text-xs uppercase tracking-[0.25em] text-blueprint">
                    Message Received
                  </p>
                  <p className="mt-4 font-display text-4xl font-extrabold uppercase text-white">
                    We'll be in touch.
                  </p>
                  <p className="mt-3 max-w-sm text-sm text-slate-400">
                    Thanks for reaching out. For anything urgent, call{" "}
                    <a href={COMPANY.phoneHref} className="text-blueprint">{COMPANY.phone}</a>.
                  </p>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="contact-form" className="border border-white/10 bg-granite/60 p-6 sm:p-8">
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label htmlFor="c-name" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Full Name *</label>
                      <input id="c-name" data-testid="contact-input-name" required value={form.fullName} onChange={set("fullName")} className={inputCls} placeholder="Jane Doe" />
                    </div>
                    <div>
                      <label htmlFor="c-email" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Email *</label>
                      <input id="c-email" type="email" data-testid="contact-input-email" required value={form.email} onChange={set("email")} className={inputCls} placeholder="you@email.com" />
                    </div>
                    <div>
                      <label htmlFor="c-phone" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Phone</label>
                      <input id="c-phone" type="tel" data-testid="contact-input-phone" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="(805) 555-0100" />
                    </div>
                    <div>
                      <label htmlFor="c-subject" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Reason for Contact *</label>
                      <select id="c-subject" data-testid="contact-select-subject" required value={form.subject} onChange={set("subject")} className={inputCls}>
                        <option value="" disabled>Select a reason</option>
                        {SUBJECTS.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <label htmlFor="c-message" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Message *</label>
                      <textarea id="c-message" rows={6} data-testid="contact-input-message" required value={form.message} onChange={set("message")} className={inputCls} placeholder="How can we help?" />
                    </div>
                  </div>
                  <button
                    type="submit"
                    data-testid="contact-submit-button"
                    disabled={submitting}
                    className="mt-6 inline-flex w-full items-center justify-center gap-2 bg-brand px-6 py-4 text-sm font-semibold text-white transition-colors hover:bg-brand-light disabled:opacity-50"
                  >
                    {submitting ? "Sending…" : "Send Message"}
                    <ArrowUpRight className="h-4 w-4" />
                  </button>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
